import { Injectable, inject } from "@angular/core";
import { Chunk } from "@app/shared/interfaces/chunk/chunk.interface";
import { ChunkContext } from "../../../logic/chunk-context";
import { PythonModuleManager } from "./python-module-manager.service";
import { PythonHelper } from "../helpers/python.helper";

@Injectable({
  providedIn: 'root',
})
export class PythonModulesService {
  private moduleManager = inject(PythonModuleManager);

  private availableChunks: Chunk[] = [];
  private jsExternalModulesNamespaces: any;
  private pythonRuntime: any;

  async initialize(runtime: any, jsExternalModulesNamespaces: any) {
    this.pythonRuntime = runtime;
    this.jsExternalModulesNamespaces = jsExternalModulesNamespaces;
    await this.moduleManager.initialize(runtime, jsExternalModulesNamespaces);
  }

  async registerChunkAsModule(
    chunk: Chunk,
    code: string = chunk.content,
    context?: ChunkContext
  ): Promise<any> {
    try {
      const moduleId = this.moduleManager.sanitizeModuleName(chunk.name);
      const registrationCode = `await module_system.register_module(${JSON.stringify(moduleId)}, ${JSON.stringify(chunk.name)}, ${JSON.stringify(code)})`;
      let registration = await this.pythonRuntime.runPythonAsync(registrationCode);
      registration = PythonHelper.pythonToJs(registration, this.pythonRuntime);

      if (registration.error) {
        throw new Error(registration.error);
      }

      // Update module registry
      this.moduleManager.moduleRegistry.set(moduleId, {
        id: moduleId,
        name: chunk.name,
        code: code.trim(),
        namespace: registration.context_id,
        isLoaded: registration.is_loaded,
        lastUsed: Date.now(),
        useCount: 0,
        dependencies: [],
        exports: []
      });

      return {
        success: true,
        moduleContext: registration  // Contains module_obj, chunks_pkg, etc.
      };
    } catch (error) {
      const errorMessage = `Error registering chunk ${chunk.name} as module: ${error}`;
      if (context) {
        context.addMessage(errorMessage, 'danger');
      }
      throw error;
    }
  }

  async importModulesForChunk(content: string, context: ChunkContext): Promise<void> {
    const { dependencies } = this.moduleManager.analyzeDependencies(content);

    if (dependencies.length === 0) {
      return;
    }

    // Validate all dependencies
    await this.moduleManager.validateModuleDependencies(dependencies, context);

    // Import modules sequentially
    for (const moduleName of dependencies) {
      try {
        await this.moduleManager.importModule(moduleName, context);
        context.addMessage(`Successfully imported module: ${moduleName}`, 'info');
      } catch (error) {
        const errorMessage = `Error importing module ${moduleName}: ${error}`;
        context.addMessage(errorMessage, 'danger');
        throw error;
      }
    }
  }

  async updateChunkModule(
    chunk: Chunk,
    context: ChunkContext
  ): Promise<boolean> {
    try {
      // Re-register module
      const registration = await this.registerChunkAsModule(chunk, chunk.content, context);

      if (!registration.success) {
        throw new Error('Module update failed');
      }

      // Execute updated module code
      const result = await this.pythonRuntime.executeCode(
        chunk.content,
        null,
        registration.moduleContext.context_id
      );

      if (result.error) {
        throw new Error(result.error);
      }

      return true;
    } catch (error) {
      const errorMessage = `Error updating module for chunk ${chunk.name}: ${error}`;
      if (context) {
        context.addMessage(errorMessage, 'danger');
      }
      throw error;
    }
  }

  updateAvailableChunks(chunks: Chunk[]): void {
    this.availableChunks = chunks.filter(chunk => chunk.steptypeId === 3);

    this.availableChunks.forEach(chunk => {
      this.registerChunkAsModule(chunk, chunk.content).catch(_error => {
        // Error registering chunk
      });
    });
  }

  getModuleInfo(moduleName: string) {
    return this.moduleManager.getModuleInfo(moduleName);
  }

  listChunkModules(): Array<{ id: string, name: string, exports: string[] }> {
    return this.moduleManager.listModules();
  }

  hasModule(moduleName: string): boolean {
    return this.moduleManager.getModuleInfo(moduleName) !== undefined;
  }

  async diagnoseSystemState(): Promise<void> {
    return this.moduleManager.diagnoseSystemState();
  }
}
